// Navbar internals split out for clarity · the file holds the
// MEGA-MENU rendering pieces (desktop ribbon, mobile column list).
// They were originally inline in Navbar.jsx; moving them here lets us
// keep Navbar.jsx focused on the chrome (logo, trigger buttons, mobile
// overlay shell, auth menu).
//
// Public exports:
//   • MegaRibbon   · full-width desktop slide-down panel
//   • PLATFORM_MAP · lookup of platform meta for mobile platform tiles
//
// Both are stateless w.r.t. the outer Navbar · they receive a menu
// entry + onClose callback and render themselves.

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PLATFORMS } from "@/data/site";
import { prefetchProps } from "@/lib/prefetch";

/* Lookup map for platform meta (logo + brand accent) · used when a
   mega-menu item carries `platformId`, so platform links render with
   the real product logo and that product's brand colour instead of
   a generic Lucide icon. */
const PLATFORM_MAP = Object.fromEntries(PLATFORMS.map((p) => [p.id, p]));

/* Full-width slide-down mega-menu ribbon. Anchored to the navbar's
   bottom edge so it always spans the full viewport width and cannot
   drift off-centre. Layout · column grid of links + right-side
   spotlight tile. */
export function MegaRibbon({ entry, onClose }) {
  const { t } = useTranslation();
  const spotlight = entry.spotlight;
  const SpotIcon = spotlight?.icon;
  const linkColCount = entry.columns.length;
  // With 4 link columns becoming the standard, the spotlight gets a
  // touch narrower so the link cols don't get squeezed below ~210px.
  // 240 keeps the spotlight readable (eyebrow + title + 2-line body
  // + CTA) without dominating the panel.
  const gridCols = spotlight
    ? `repeat(${linkColCount}, minmax(0, 1fr)) 240px`
    : `repeat(${linkColCount}, minmax(0, 1fr))`;
  return (
    <motion.div
      key={entry.key}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="absolute inset-x-0 top-full"
      data-testid={`mega-ribbon-${entry.key}`}
    >
      <div
        className="border-t border-b"
        style={{
          background: "var(--surface)",
          backgroundImage: "linear-gradient(180deg, color-mix(in srgb, var(--brand) 3%, transparent) 0%, transparent 60%)",
          borderTopColor: "color-mix(in srgb, var(--brand) 18%, transparent)",
          borderBottomColor: "color-mix(in srgb, var(--brand) 12%, transparent)",
          boxShadow: "var(--shadow-panel)",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5">
          <div className="grid gap-x-6 gap-y-4 items-start" style={{ gridTemplateColumns: gridCols }}>
            {entry.columns.map((col) => (
              <div key={col.heading} className="min-w-0">
                <div className="text-[12px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-2 pl-1">
                  {col.heading}
                </div>
                <div className="flex flex-col gap-0.5">
                  {col.items.map((it) => {
                    const plat = it.platformId ? PLATFORM_MAP[it.platformId] : null;
                    const isPromo = it.promo === true;
                    return (
                      <Link
                        key={it.label}
                        to={it.to}
                        data-testid={`mega-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        {...prefetchProps(it.to.split("?")[0])}
                        onClick={onClose}
                        className={`group flex items-center gap-3 px-2 py-1.5 rounded-lg transition-colors ${
                          isPromo
                            ? "px-3 py-2 relative" // promo gets a touch more breathing room since it now sits inside a gold pill
                            : "hover:bg-[color:var(--surface-2)]"
                        }`}
                        style={
                          isPromo
                            ? {
                                // STRONG gold pill treatment so the bonus
                                // link is unmissable inside the Funding
                                // column. Bumped border + tint + outer
                                // glow compared to the earlier subtle pass.
                                background:
                                  "linear-gradient(135deg, color-mix(in srgb, var(--gold-2) 16%, transparent) 0%, color-mix(in srgb, var(--gold-2) 6%, transparent) 100%)",
                                border:
                                  "1.5px solid color-mix(in srgb, var(--gold-2) 75%, transparent)",
                                boxShadow:
                                  "0 0 0 1px color-mix(in srgb, var(--gold-2) 30%, transparent), 0 8px 22px -10px color-mix(in srgb, var(--gold-2) 65%, transparent), inset 0 1px 0 color-mix(in srgb, var(--gold-2) 35%, transparent)",
                              }
                            : undefined
                        }
                      >
                        {/* Naked icon · no surrounding tinted box, just the
                            mark itself. Platform logos sit in a small white
                            disc only when the brand mark needs contrast. */}
                        <span className="shrink-0 inline-flex w-5 h-5 items-center justify-center">
                          {plat ? (
                            <img
                              src={plat.logo}
                              alt={plat.name}
                              className="w-5 h-5 object-contain"
                              style={{
                                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.25))",
                              }}
                              loading="lazy"
                            />
                          ) : it.IcomoonName ? (
                            <i className={`icon-${it.IcomoonName} text-brand`} style={{ fontSize: 18, lineHeight: 1 }} aria-hidden />
                          ) : (
                            <it.Icon size={16} strokeWidth={1.7} className={`${isPromo ? "text-[color:var(--gold-2)]" : "text-brand"} transition-transform group-hover:scale-110`} />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span
                              className="block text-[13px] font-medium text-[color:var(--text-1)] truncate leading-tight transition-colors"
                              style={plat ? { "--hover-color": plat.accent } : undefined}
                            >
                              {it.label}
                            </span>
                            {isPromo && (
                              <span
                                className="font-mono text-[8.5px] uppercase tracking-[0.2em] px-1.5 py-[1px] rounded-full shrink-0 leading-none"
                                style={{
                                  background: "color-mix(in srgb, var(--gold-2) 14%, transparent)",
                                  border: "1px solid color-mix(in srgb, var(--gold-2) 55%, transparent)",
                                  color: "var(--gold-2)",
                                }}
                                aria-hidden
                              >
                                {t("nav.limited", "Limited")}
                              </span>
                            )}
                          </span>
                          <span className="block text-[12px] text-[color:var(--text-3)] truncate font-light leading-tight">
                            {it.tagline}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}

            {spotlight && (
              <Link
                to={spotlight.cta.to}
                onClick={onClose}
                data-testid={`mega-spotlight-${entry.key}`}
                className="group relative overflow-hidden rounded-xl p-4 flex flex-col justify-between min-h-[170px] transition-transform"
                style={{
                  background:
                    "linear-gradient(135deg, color-mix(in srgb, var(--brand) 14%, var(--surface-2)) 0%, var(--surface) 60%, color-mix(in srgb, var(--brand-deep) 18%, var(--surface-2)) 100%)",
                  border: "1px solid color-mix(in srgb, var(--brand) 32%, transparent)",
                  boxShadow: "var(--shadow-md)",
                }}
              >
                {/* Decorative scan-grid */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.18] pointer-events-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(0deg, transparent 95%, color-mix(in srgb, var(--brand) 50%, transparent) 100%), linear-gradient(90deg, transparent 95%, color-mix(in srgb, var(--brand) 30%, transparent) 100%)",
                    backgroundSize: "22px 22px",
                  }}
                />
                {/* Brand glow blob */}
                <div
                  aria-hidden
                  className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
                  style={{
                    background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 28%, transparent) 0%, transparent 70%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    {SpotIcon && (
                      <SpotIcon size={16} strokeWidth={1.8} className="text-brand shrink-0" />
                    )}
                    <span className="text-[12px] uppercase tracking-[0.22em] text-brand font-semibold">{spotlight.eyebrow}</span>
                  </div>
                  <div className="font-display text-[15px] font-medium text-[color:var(--text-1)] leading-snug mb-1.5">
                    {spotlight.title}
                  </div>
                  <div className="text-[12px] text-[color:var(--text-2)] font-light leading-snug">{spotlight.body}</div>
                </div>

                {/* Platform logos row for the platforms spotlight · clean,
                    no surrounding boxes */}
                {spotlight.kind === "platforms" && spotlight.platforms && (
                  <div className="relative mt-3 flex items-center gap-3">
                    {spotlight.platforms.map((p) => (
                      <img
                        key={p.id}
                        src={p.logo}
                        alt={p.name}
                        className="h-6 w-auto object-contain opacity-90"
                        style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.35))" }}
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}

                <div className="relative mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-brand group-hover:gap-2 transition-all">
                  {spotlight.cta.label}
                  <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* PlatformLink helper used by mobile menu items that carry `platformId` ·
   exported so MobileMenu (in Navbar.jsx) can render platform tiles with
   the brand-correct logo and accent border. */
export { PLATFORM_MAP };
